import type { SuccessContentProps } from 'butlerapp-integration/src/Pages/SuccessPage';
import { useEffect, useState, type PropsWithChildren } from 'react';
import type { IAccordionItemProps } from 'butlerapp-integration/src/components/Accordion/Accordion';
import { StyleSheet } from 'react-native-unistyles';
import { AccordionItem, Icon, Link, Text, VStack } from '~/molecules';
import { HtmlContent as RenderHtml } from '~/components/RenderHtml';
import { FlashNotifications } from 'butlerapp-integration/src/components/Notification/FlashNotifications';
import { Message } from 'butlerapp-integration/src/Pages/OutgoingMessagesPage/Message';
import type { OutgoingMessageModel } from 'butlerapp-integration/src/store/manager/models/OutgoingMessages/OutgoingMessageModel';
import { useBookingFormDialog } from '../BookingFormDialogContext';

type SuccessMessageProps = {
    message: OutgoingMessageModel;
    index: number;
};

export const OutgoingMessage = ({
    title,
    defaultCollapsed = false,
    children,
}: PropsWithChildren<IAccordionItemProps>) => {
    const [isExpanded, setIsExpanded] = useState(!defaultCollapsed);
    return (
        <AccordionItem expanded={isExpanded} setExpanded={setIsExpanded} style={styles.messageCard}>
            <AccordionItem.Header
                role="button"
                aria-expanded={isExpanded}
                style={styles.messageHeaderContent}
                contentStyle={styles.messageTitle}
                left={<Icon name="email-outline" size={20} style={styles.headerIcon} type="material-community" />}
                right={<Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} size={22} style={styles.chevron} type="material-community" />}
            >
                {title}
            </AccordionItem.Header>
            <AccordionItem.Content style={styles.messageBody}>
                {typeof children === 'string' ? <RenderHtml>{children}</RenderHtml> : children}
            </AccordionItem.Content>
        </AccordionItem>
    );
};

export const Attachments = ({ attachments = {} }: { attachments?: Record<string, string> }) => {
    const files = Object.entries(attachments);
    if (!files.length) return null;
    return (
        <VStack style={styles.attachments}>
            {files.map(([name, url]) => (
                <Link href={url} key={name} style={styles.attachment} target="_blank">
                    <Icon
                        name={name.toLowerCase().endsWith('.pdf') ? 'file-pdf-box' : 'paperclip'}
                        size={22}
                        style={styles.attachmentIcon}
                        type="material-community"
                    />
                    <Text numberOfLines={2} style={styles.attachmentName}>
                        {name}
                    </Text>
                </Link>
            ))}
        </VStack>
    );
};

const SuccessMessage = ({ message, index }: SuccessMessageProps) => (
    <Message
        title={message.get<string>('subject', 'Message')}
        index={index}
        defaultCollapsed={index > 0}
        item={message}
    />
);

export const SuccessContent = ({ flashNotifications = [], messages, title }: SuccessContentProps) => {
    const { onSuccess } = useBookingFormDialog();
    useEffect(() => {
        onSuccess?.();
    }, [onSuccess]);

    return (
        <VStack style={styles.root}>
            <FlashNotifications flashNotifications={flashNotifications} shouldScrollToRef />

            {title ? (
                <Text role="heading" style={styles.title}>
                    {title}
                </Text>
            ) : null}

            <VStack style={styles.messages}>
                {messages.map((message, index) => (
                    <SuccessMessage key={message.id} message={message} index={index} />
                ))}
            </VStack>
        </VStack>
    );
};

const styles = StyleSheet.create((theme) => ({
    root: {
        gap: theme.spacings['3'],
        width: '100%',
    },
    title: {
        ...theme.typescale.titleMedium,
        color: theme.colors.onSurface,
        fontWeight: '600',
    },
    messages: {
        gap: theme.spacings['3'],
        width: '100%',
    },
    messageCard: {
        backgroundColor: theme.colors.surface,
        borderColor: theme.colors.outline,
        borderRadius: theme.shapes.corner.small,
        borderWidth: 1,
        overflow: 'hidden',
        width: '100%',
    },
    messageHeaderContent: {
        alignItems: 'center',
        gap: theme.spacings['2'],
        minHeight: 52,
        paddingHorizontal: theme.spacings['4'],
        paddingVertical: theme.spacings['3'],
        width: '100%',
    },
    headerIcon: {
        color: theme.colors.onSurfaceMuted,
    },
    messageTitle: {
        ...theme.typescale.titleSmall,
        color: theme.colors.onSurface,
        flex: 1,
        fontWeight: '600',
    },
    chevron: {
        color: theme.colors.onSurfaceMuted,
    },
    messageBody: {
        borderColor: theme.colors.lines,
        borderTopWidth: 1,
        gap: theme.spacings['4'],
        padding: theme.spacings['4'],
        width: '100%',
    },
    attachments: {
        borderColor: theme.colors.lines,
        borderTopWidth: 1,
        gap: theme.spacings['2'],
        paddingTop: theme.spacings['3'],
        width: '100%',
    },
    attachment: {
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        borderColor: theme.colors.outline,
        borderRadius: theme.shapes.corner.small,
        borderWidth: 1,
        flexDirection: 'row',
        gap: theme.spacings['2'],
        maxWidth: '100%',
        paddingHorizontal: theme.spacings['3'],
        paddingVertical: theme.spacings['2'],
    },
    attachmentIcon: {
        color: theme.colors.onSurfaceMuted,
        flexShrink: 0,
    },
    attachmentName: {
        color: theme.colors.onSurface,
        flexShrink: 1,
        textDecorationLine: 'underline',
    },
}));
